import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { Sidebar } from '../components/Sidebar';
import { AvatarUpload } from '../components/AvatarUpload';
import { userService } from '../services/api';
import { useFamilyNames, useUserDetail } from '../hooks/useSharedData';
import '../styles/layout.scss';

export const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { toggleSidebar } = useSidebar();
  const [profile, setProfile] = useState<any>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { data: familyNames } = useFamilyNames();
  const { data: userDetail, loading: userDetailLoading } = useUserDetail(user?.id);

  const [formData, setFormData] = useState({
    fullName: '',
    homeName: '',
    email: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    gender: '',
    spouseName: '',
    spouseImageUrl: '',
    spouseBirthDay: '',
    spouseBirthMonth: '',
    spouseBirthYear: '',
    spouseDiedAt: '',
    spouseDiedYear: '',
    spouseIsAlive: true,
    familyNameId: '',
  });

  useEffect(() => {
    if (userDetail) {
      setProfile(userDetail);
      setFormData({
        fullName: userDetail.fullName || '',
        homeName: userDetail.homeName || '',
        email: userDetail.email || '',
        birthDay: userDetail.birthDay?.toString() || '',
        birthMonth: userDetail.birthMonth?.toString() || '',
        birthYear: userDetail.birthYear?.toString() || '',
        gender: userDetail.gender || '',
        spouseName: userDetail.spouseName || '',
        spouseImageUrl: userDetail.spouseImageUrl || '',
        spouseBirthDay: userDetail.spouseBirthDay?.toString() || '',
        spouseBirthMonth: userDetail.spouseBirthMonth?.toString() || '',
        spouseBirthYear: userDetail.spouseBirthYear?.toString() || '',
        spouseDiedAt: userDetail.spouseDiedAt || '',
        spouseDiedYear: userDetail.spouseDiedYear?.toString() || '',
        spouseIsAlive: userDetail.spouseIsAlive !== false,
        familyNameId: userDetail.familyNameId?.toString() || '',
      });
    }
  }, [userDetail]);

  const roleLabel = useMemo(() => {
    if (!profile) return 'Người dùng';
    if (profile.isAdmin) return 'System Admin';
    if (profile.isFamilyAdmin) return 'Family Admin';
    return 'Người dùng thường';
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: e.target instanceof HTMLInputElement && e.target.type === 'checkbox' ? e.target.checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    try {
      setIsSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        spouseName: formData.spouseName || undefined,
        clearSpouseName: !formData.spouseName,
        spouseImageUrl: formData.spouseImageUrl || undefined,
        spouseBirthDay: formData.spouseBirthDay ? Number(formData.spouseBirthDay) : undefined,
        spouseBirthMonth: formData.spouseBirthMonth ? Number(formData.spouseBirthMonth) : undefined,
        spouseBirthYear: formData.spouseBirthYear ? Number(formData.spouseBirthYear) : undefined,
        clearSpouseBirthDay: !formData.spouseBirthDay,
        clearSpouseBirthMonth: !formData.spouseBirthMonth,
        clearSpouseBirthYear: !formData.spouseBirthYear,
        spouseDiedAt: formData.spouseDiedAt || undefined,
        clearSpouseDiedAt: !formData.spouseDiedAt,
        spouseDiedYear: formData.spouseDiedYear ? Number(formData.spouseDiedYear) : null,
        clearSpouseDiedYear: !formData.spouseDiedYear,
        spouseIsAlive: formData.spouseIsAlive,
      };

      const response = await userService.updateUser(user.id, payload);
      if (response.success) {
        const updatedUser = response.data;
        setProfile(updatedUser);
        setSuccess('Cập nhật thông tin cá nhân thành công.');
        updateUser({
          fullName: updatedUser.fullName,
          email: updatedUser.email,
          familyNameId: updatedUser.familyNameId,
          isAdmin: updatedUser.isAdmin,
          isFamilyAdmin: updatedUser.isFamilyAdmin,
        });
      } else {
        setError(response.message || 'Không thể cập nhật thông tin');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Không thể cập nhật thông tin');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarUploadSuccess = (imageUrl: string) => {
    setProfile((current: any) => ({ ...current, imageUrl }));
    updateUser({
      fullName: user?.fullName || '',
      email: user?.email || '',
      familyNameId: user?.familyNameId || 0,
      isAdmin: user?.isAdmin || false,
      isFamilyAdmin: user?.isFamilyAdmin || false,
    });
  };

  const handleSpouseImageUploadSuccess = (imageUrl: string) => {
    setFormData((prev) => ({ ...prev, spouseImageUrl: imageUrl }));
    setProfile((current: any) => ({ ...current, spouseImageUrl: imageUrl }));
    setSuccess('Ảnh vợ/chồng đã được lưu thành công.');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="main-content">
        <div className="header">
          <button className="hamburger-btn" onClick={toggleSidebar} aria-label="Toggle sidebar">
            <i className="fas fa-bars"></i>
          </button>
          <h1>
            <i className="fas fa-user-circle"></i> Thông tin cá nhân
          </h1>
        </div>

        <div className="content-area">
          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {userDetailLoading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="profile-page-card">
              {profile && (
                <div className="profile-upload-top">
                  <AvatarUpload
                    userId={user.id}
                    currentImageUrl={profile.imageUrl}
                    onUploadSuccess={handleAvatarUploadSuccess}
                    onError={setError}
                  />
                </div>
              )}

              <form className="profile-form" onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="fullName">Họ và tên</label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={handleChange}
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="homeName">Tên gọi ở nhà</label>
                    <input
                      id="homeName"
                      name="homeName"
                      type="text"
                      value={formData.homeName}
                      onChange={handleChange}
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="familyNameId">Dòng họ</label>
                    <select
                      id="familyNameId"
                      name="familyNameId"
                      value={formData.familyNameId}
                      onChange={handleChange}
                      disabled
                    >
                      <option value="">Chọn dòng họ</option>
                      {familyNames.map((family) => (
                        <option key={family.id} value={family.id}>
                          {family.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="gender">Giới tính</label>
                    <select id="gender" name="gender" value={formData.gender} onChange={handleChange} disabled>
                      <option value="">Chọn giới tính</option>
                      <option value="Male">Nam</option>
                      <option value="Female">Nữ</option>
                      <option value="Other">Khác</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Ngày sinh</label>
                    <div className="form-inline-fields">
                      <input type="number" value={formData.birthDay} placeholder="Ngày" readOnly disabled />
                      <input type="number" value={formData.birthMonth} placeholder="Tháng" readOnly disabled />
                      <input type="number" value={formData.birthYear} placeholder="Năm" readOnly disabled />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="spouseName">Tên vợ/chồng</label>
                    <input
                      id="spouseName"
                      name="spouseName"
                      type="text"
                      value={formData.spouseName}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Ảnh vợ/chồng</label>
                    <AvatarUpload
                      userId={user.id}
                      isSpouseImage={true}
                      currentImageUrl={profile?.spouseImageUrl}
                      onUploadSuccess={handleSpouseImageUploadSuccess}
                      onError={setError}
                    />
                  </div>

                  <div className="form-group">
                    <label>Năm sinh vợ/chồng</label>
                    <div className="form-inline-fields">
                      <input type="number" name="spouseBirthDay" min="1" max="31" placeholder="Ngày" value={formData.spouseBirthDay} onChange={handleChange} />
                      <input type="number" name="spouseBirthMonth" min="1" max="12" placeholder="Tháng" value={formData.spouseBirthMonth} onChange={handleChange} />
                      <input type="number" name="spouseBirthYear" min="1" max="2100" placeholder="Năm" value={formData.spouseBirthYear} onChange={handleChange} />
                    </div>
                  </div>

                  <label className="form-checkbox">
                    <input type="checkbox" name="spouseIsAlive" checked={formData.spouseIsAlive} onChange={handleChange} />
                    <span>Vợ/chồng đang sinh sống</span>
                  </label>

                  {!formData.spouseIsAlive && <div className="death-date-fields">
                    <div className="form-group">
                      <label htmlFor="spouseDiedAt">Ngày mất vợ/chồng (âm lịch)</label>
                      <input id="spouseDiedAt" name="spouseDiedAt" type="text" value={formData.spouseDiedAt} onChange={handleChange} />
                    </div>

                    <div className="form-group">
                      <label htmlFor="spouseDiedYear">Năm mất (dương lịch)</label>
                      <input id="spouseDiedYear" name="spouseDiedYear" type="number" min="1" max="2100" value={formData.spouseDiedYear} onChange={handleChange} />
                    </div>
                  </div>}
                </div>

                <div className="profile-actions">
                  <button type="submit" className="btn btn-primary" disabled={isSaving}>
                    {isSaving ? 'Đang lưu...' : 'Lưu thay đổi'}
                  </button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.55rem 1rem',
                      borderRadius: '999px',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      backgroundColor: profile?.isAdmin
                        ? '#fff3cd'
                        : profile?.isFamilyAdmin
                          ? '#d1f2eb'
                          : '#eaf2ff',
                      color: profile?.isAdmin
                        ? '#8a6d3b'
                        : profile?.isFamilyAdmin
                          ? '#0b7a5c'
                          : '#235789',
                      border: '1px solid',
                      borderColor: profile?.isAdmin
                        ? '#f4d07a'
                        : profile?.isFamilyAdmin
                          ? '#8ad8bc'
                          : '#bfd8f7',
                    }}
                  >
                    <i
                      className={`fas ${profile?.isAdmin ? 'fa-shield-alt' : profile?.isFamilyAdmin ? 'fa-users-cog' : 'fa-user'}`}
                    ></i>
                    {roleLabel}
                  </span>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
